import ClevryImage from "assets/clevry.webp";
import GoogleTrustServicesImage from "assets/google-trust-services.webp";
import LukeImage from "assets/luke-griffiths.webp";
import { Block } from "components/block/block";
import { Content } from "components/content/content";
import { IconButton } from "components/icons/icon-button";
import { EmailIcon, GithubIcon, LinkedinIcon } from "components/icons/icons";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { state } from "store/store";

import FutropolisImage from "assets/futropolis.webp";
import GateImage from "assets/gate.webp";
import MatteoZamagniImage from "assets/matteozamagni.webp";
import RyskImage from "assets/rysk.webp";
import HakiImage from "assets/haki.webp";

import { useIsMobile } from "helpers/useIsMobile";
import { ThemeContext, TweakContext } from "App";

const colors = ["#2ae9bc", "#99ff00"];

export const Pages: React.FC = () => {
  const isMobile = useIsMobile();
  const { isAccesibilityMode } = useContext(ThemeContext);
  const isSimpleLayout = isMobile || isAccesibilityMode;

  const scrollArea = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = document.querySelector(
      ".scrollArea"
    ) as HTMLDivElement | null;
    scrollArea.current = element;
  }, []);

  const handleImageScroll = useCallback(
    (e: React.WheelEvent<HTMLAnchorElement | HTMLDivElement>) => {
      if (scrollArea.current) {
        if (e.type === "wheel") {
          const event = e as React.WheelEvent<HTMLAnchorElement>;
          scrollArea.current.scrollTop += event.deltaY / 2;
        }
      }
    },
    [scrollArea]
  );

  return (
    <>
      <Block factor={1} offset={isSimpleLayout ? -0.1 : 0}>
        <Content side="left" className="portfolio-block" color={colors[0]}>
          <h2 className="bg-white">Hari Chauhan</h2>
          <h4>
            {"I'm"} a London-based <span className="accent">developer</span>{" "}
            with a broad range of{" "}
            <span className="accent">technical experience</span>, and excellent{" "}
            <span className="accent">management skills</span> required to take
            projects from <span className="accent">start to finish</span>.{" "}
          </h4>
          <h4 style={{ alignSelf: "flex-end" }} className="text-right ">
            With a background in{" "}
            <span className="accent">software engineering</span>, I have worked
            within a diverse range of fields including{" "}
            <span className="accent">3D graphics, AI and Web3</span> for a range
            of clients including <span className="accent">Google</span> and{" "}
            <span className="accent">The Financial Times</span>.
          </h4>
          <h4 style={{ alignSelf: "flex-end" }}>
            Currently freelancing, I am always{" "}
            <span className="accent">seeking new opportunities</span> to apply
            existing knowledge and gain new skills.
          </h4>
          <h4 style={{ color: "var(--blue-600)" }}>
            Projects of note are included below.
          </h4>
          <div className="bottom-bar">
            <div className="socials">
              <IconButton
                icon={GithubIcon}
                href="https://github.com/haric95"
                className="icon"
              />
              <IconButton
                icon={LinkedinIcon}
                href="https://www.linkedin.com/in/hari-chauhan-a07213122/"
                className="icon"
              />
              <IconButton
                icon={EmailIcon}
                href="mailto:hari-c@hotmail.co.uk"
                className="icon"
              />
            </div>
          </div>
        </Content>
      </Block>
      <Block factor={isSimpleLayout ? 1 : -1} offset={1}>
        <Content side="right" className="portfolio-block" color={colors[1]}>
          <h2>Matteo Zamagni Portfolio</h2>
          <h4 style={{ color: "var(--blue-600)" }}>
            I was contracted to develop the website alongside design studio
            Nufolklore. This included
            <span className="accent-2"> front-end development</span>, production
            of a user-friendly{" "}
            <span className="accent-2">content management system</span> and{" "}
            <span className="accent-2">deployment</span> onto the web.
          </h4>
          <h4 style={{ color: "var(--blue-600)" }} className="text-right">
            Technologies of note:{" "}
            <span className="accent-2">React, Strapi, Tailwind, NextJS</span>
          </h4>
          <a
            className="image-container unstyled-button site-link"
            href="https://matteozamagni.com"
            target="_blank"
            rel="noreferrer"
            onWheel={(e) => handleImageScroll(e)}
          >
            <img src={MatteoZamagniImage} alt="Matteo Zamagni Image" />
          </a>
        </Content>
      </Block>
      <Block factor={1} offset={2}>
        <Content side="left" className="portfolio-block" color={colors[0]}>
          <h2>Rysk Finance</h2>
          <h4 style={{ color: "var(--blue-600)" }}>
            An <span className="accent">ethereum</span>-based decentralised
            finance application.
          </h4>
          <h4 style={{ color: "var(--blue-600)" }} className="text-right">
            I was contracted to lead{" "}
            <span className="accent">front-end development</span> on the
            application dashboard as well as the marketing site.
          </h4>
          <h4 style={{ color: "var(--blue-600)" }}>
            Technologies of note:{" "}
            <span className="accent">React, Ethers, Tailwind, GLSL</span>
          </h4>
          <a
            className="image-container unstyled-button site-link"
            href="https://rysk.finance"
            target="_blank"
            rel="noreferrer"
            onWheel={(e) => handleImageScroll(e)}
          >
            <img src={RyskImage} alt="Rysk" />
          </a>
        </Content>
      </Block>
      <Block factor={isSimpleLayout ? 1 : -1} offset={3}>
        <Content side="right" className="portfolio-block" color={colors[1]}>
          <h2>Gather All The Electronics</h2>
          <h4 style={{ color: "var(--blue-600)" }}>
            The <span className="accent-2">GATE executants</span> are a group of
            creators based in East London, of which I am a member.
          </h4>

          <h4 style={{ color: "var(--blue-600)" }} className="text-right">
            Our online home was designed and developed by myself autonomously.
            It includes a DIY ticketing platform as well as an archive of all of
            our events and shows. Some of my other contributions include{" "}
            <span className="accent-2">audio-reactive visuals</span> built in{" "}
            <span className="accent-2">MaxMSP</span>, as well as{" "}
            <span className="accent-2">cooking</span> for the 100+ attendees of
            our events.
          </h4>
          <a
            className="image-container unstyled-button site-link"
            href="https://gatheralltheelectronics.com"
            target="_blank"
            rel="noreferrer"
            onWheel={(e) => handleImageScroll(e)}
          >
            <img src={GateImage} alt="Propagate" />
          </a>
        </Content>
      </Block>
      <Block factor={1} offset={4}>
        <Content side="left" color={colors[0]} className="portfolio-block">
          <h3>Other Web Development</h3>
          <div className="others">
            <div className="other">
              <a
                className="image-container unstyled-button site-link other-site-link"
                href="https://pki.goog"
                target="_blank"
                rel="noreferrer"
                onWheel={(e) => handleImageScroll(e)}
              >
                <img src={GoogleTrustServicesImage} alt="Clevry" />
              </a>
              <h4 style={{ color: "var(--blue-600)" }}>
                <span className="accent">Google’s PKI site</span>. Built using
                {"Google's"} internal Glue Framework whilst working for Phantom
                digital agency.
              </h4>
            </div>
            <div className="other">
              <a
                className="image-container unstyled-button site-link other-site-link"
                href="https://lukegriffiths.co.uk"
                target="_blank"
                rel="noreferrer"
                onWheel={(e) => handleImageScroll(e)}
              >
                <img src={LukeImage} alt="Luke Griffiths" />
              </a>
              <h4 style={{ color: "var(--blue-600)" }}>
                A <span className="accent">portfolio site</span> for
                London-based Graphic Designer{" "}
                <span className="accent">Luke Griffiths</span>
              </h4>
            </div>
            <div className="other">
              <a
                className="image-container unstyled-button site-link other-site-link pointer-events-none"
                href=""
                onWheel={(e) => handleImageScroll(e)}
              >
                <img src={FutropolisImage} alt="Futropolis" />
              </a>
              <h4 style={{ color: "var(--blue-600)" }}>
                Front-end engineering for the Financial Times project in
                partnership with Honeywell. Making use of{" "}
                <span className="accent">Three.JS</span> for web 3D graphics.
              </h4>
            </div>
          </div>
        </Content>
      </Block>
    </>
  );
};
